import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { BaseRespone } from '../models/common.response';
import * as jwt_decode from 'jwt-decode';
@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(credentials: { email: string }): Observable<any> {
        return this._httpClient.post(`${environment.baseUrl}/Auth/forgot-password`, credentials).pipe(
            switchMap((response: any) => {
                return of(response);
            })
        );
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(credentials: { password: string, email: string, token: string }): Observable<any> {
        return this._httpClient.post(`${environment.baseUrl}/Auth/reset-password`, credentials).pipe(
            switchMap((response: any) => {
                return of(response);
            })
        );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { userName: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(`${environment.baseUrl}/Auth/login`, credentials).pipe(
            switchMap((response: any) => {

                if (response.result.requiresOtpVerification) {
                    // If OTP is required, don't set the user or token yet
                    return of(response);
                }

                // Store the access token in the local storage
                this.accessToken = response.result.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    validateOtp(credentials: { transactionId: string, otp: string }): Observable<any> {
        return this._httpClient.post(`${environment.baseUrl}/Auth/otp/validate`, credentials).pipe(
            switchMap((response: any) => {

                if (response.result.requiresOtpVerification) {
                    // If OTP is required, don't set the user or token yet
                    return of(response);
                }

                // Store the access token in the local storage
                this.accessToken = response.result.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    resendOtp(transactionId: string): Observable<any> {
        const obj = {
            transactionId: transactionId
        }
        return this._httpClient.post(`${environment.baseUrl}/Auth/otp/resend`, obj).pipe(
            switchMap((response: any) => {
                // Return a new observable with the response
                return of(response);
            })
        );
    }
    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post('api/auth/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        // if ( AuthUtils.isTokenExpired(this.accessToken) )
        // {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        return of(true);// this.signInUsingToken();
    }

    generateTokenFromCode(data: any): Observable<BaseRespone<any>> {
        return this._httpClient.post<BaseRespone<any>>(`${environment.baseUrl}/company/configuration`, data)
    }

    getRolesFromToken() {
        var decodeToken = jwt_decode.jwtDecode(localStorage.getItem("accessToken"));
        if (decodeToken != null) {
            var roles =
                decodeToken[
                'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
                ];
            return roles;
        }
        return null;
    }
}
